import { useCallback, useEffect, useState } from 'react'

/**
 * manages follow/unfollow state and actions for a subject (user, group, etc.)
 * resets optimistic state when subjectId changes, reusable across profile pages,
 * event volunteer lists, group members, etc.

 */
export default function useFollowState({
  subjectId,
  initialFollowing = false,
  followApi,
  unfollowApi,
  onSuccess,
}) {
  const [optimisticFollowing, setOptimisticFollowing] = useState(null)
  const [followLoading, setFollowLoading] = useState(false)

  useEffect(() => {
    setOptimisticFollowing(null)
  }, [subjectId])

  const isFollowing = optimisticFollowing ?? initialFollowing ?? false

  const onFollowClick = useCallback(async () => {
    if (subjectId == null || followLoading) return
    const nextFollowing = !isFollowing
    setOptimisticFollowing(nextFollowing)
    setFollowLoading(true)
    try {
      const response = nextFollowing
        ? await followApi({ id: subjectId })
        : await unfollowApi({ id: subjectId })
      const status = response?.status ?? response?.response?.status
      const success = status == null || (status >= 200 && status < 300)
      if (!success) {
        setOptimisticFollowing(null)
      } else if (onSuccess) {
        onSuccess(nextFollowing)
      }
    } catch {
      setOptimisticFollowing(null)
    } finally {
      setFollowLoading(false)
    }
  }, [subjectId, isFollowing, followLoading, followApi, unfollowApi, onSuccess])

  return { isFollowing, followLoading, onFollowClick }
}
